<template>
  <div
      :class="{ 'is-panel': variant === 'panel' }"
      class="font-family-selection"
      @mousedown.stop
      @keydown.stop="">
    <div
        v-if="variant === 'panel'"
        class="lp-font">
      <button
          ref="trigger"
          :class="{ 'is-open': isPanelOpen }"
          type="button"
          class="lp-font__trigger"
          @click="togglePanel">
        <span class="lp-font__current">{{ getValue.name || label }}</span>
        <icon
            name="chevron"
            class="lp-font__chevron"/>
      </button>
      <div
          v-if="isPanelOpen"
          class="lp-font__pop"
          @click.stop>
        <div class="lp-font__search">
          <icon name="search"/>
          <input
              ref="search"
              v-model="search"
              type="text"
              placeholder="Search fonts"
              spellcheck="false"
              @keydown.esc="closePanel">
        </div>
        <div class="lp-font__list">
          <template v-for="(option, index) in filteredOptions">
            <div
                v-if="option.isSection"
                :key="'section-' + index"
                class="lp-font__section">{{ panelSectionLabel(option.label) }}</div>
            <div
                v-else
                :key="'font-' + index + '-' + option.name"
                :class="{ 'is-current': option.name === getValue.name, 'is-preview': previewName === option.name }"
                class="lp-font__item full-option"
                @mouseenter="onPanelPreview(option)"
                @mouseleave="onPanelPreviewEnd"
                @click="pickFont(option)">
              <svg
                  v-if="option.name === getValue.name"
                  class="lp-font__check"
                  viewBox="0 0 16 16"
                  aria-hidden="true">
                <path
                    d="M3 8.5l3.2 3L13 4.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"/>
              </svg>
              <span
                  :style="{ fontFamily: JSON.stringify(option.name) }"
                  class="lp-font__name">{{ option.name }}</span>
              <span
                  v-if="previewName === option.name && option.name !== getValue.name"
                  class="lp-font__badge">Previewing</span>
              <span
                  v-tooltip="option.isFavourite ? 'Click to remove from Favourite' : 'Click to Add to Favourite'"
                  v-if="option.id && option.id.length > 0"
                  :class="{ 'is-favourite': option.isFavourite }"
                  class="lp-font__star"
                  @click.stop="changeFavourite(option.id, !option.isFavourite)">
                <icon :name="option.isFavourite ? 'star-favourite' : 'star-un-favourite'"/>
              </span>
            </div>
          </template>
          <div
              v-if="!filteredOptions.length"
              class="lp-font__empty">No results matched</div>
        </div>
      </div>
    </div>
    <label
        v-if="variant !== 'panel'"
        class="mb-4">{{ label }}</label>

    <multi-select
        v-if="variant !== 'panel'"
        :options="getOptions"
        :show-labels="false"
        :allow-empty="false"
        :preserve-search="true"
        :placeholder="getValue.name"
        :reset-after="true"
        :preselect-first="false"
        :clear-on-select="false"
        :max-height="240"
        v-model="getValue"
        label="name"
        select-label="''"
        track-by="name"
        @select="changeFontName">
      <span slot="noResult">No results matched</span>
      <template
          slot="option"
          slot-scope="props">
        <div
            class="full-option"
            @mouseenter="!props.option.isSection && previewFontFamily(props.option)"
            @mouseleave="!props.option.isSection && removePreviewFont()"
        >
          <div
              v-if="props.option.isSection"
              class="font-section-label">
            {{ props.option.label }}
          </div>
          <div
              v-else
          >
            <span :style="{fontFamily:JSON.stringify(props.option.name)}">{{ props.option.name }}</span>
            <span
                v-tooltip="'Click to remove from Favourite'"
                v-if="props.option.isFavourite && props.option.id.length > 0"
                class="star"
                @click.stop="changeFavourite(props.option.id, false)"> <icon :name="'star-favourite'"/></span>
            <span
                v-tooltip="'Click to Add to Favourite'"
                v-if="!props.option.isFavourite && props.option.id.length > 0"
                class="star"
                @click.stop="changeFavourite(props.option.id, true)"> <icon :name="'star-un-favourite'"/></span>
          </div>
        </div>

      </template>
      <template
          slot="singleLabel"
          slot-scope="props">
        <div>
          <span>{{ props.option.name }}</span>
        </div>
      </template>
    </multi-select>
  </div>
</template>
<script>
import Multiselect from 'vue-multiselect';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import axios from 'axios';
import { cloneDeep } from 'lodash'; // TDOD: Change to @/

// Section names in the left panel font list (variant="panel")
const PANEL_SECTION_LABELS = {
  'Product Fonts':  'Used in this product',
  'Favorite Fonts': 'Favorites',
  'All Fonts':      'All fonts',
};

export default {
  name:       'FontFamilySelector',
  components: { 'multi-select': Multiselect },
  props:      {
    label:   String,
    value:   String,
    product: {
      type:    Object,
      default: null,
    },
    // 'select' = vue-multiselect dropdown (default), 'panel' = left panel trigger + dropdown font list
    variant: {
      type:    String,
      default: 'select',
    },
  },
  data() {
    return {
      options:     [],
      isPanelOpen: false,
      search:      '',
      previewName: '',
    };
  },
  computed: {
    getValue: {
      get() {
        return { name: this.normalizeFontName(this.value) };
      },
      set(selected) {
        if (!selected || !selected.name) {
        }
      },
    },

    fontsUsed() {
      const selectedProduct = this.product || this.$store.getters.getSelectedProduct || {};
      const pages           = selectedProduct.pages || [];
      const fontsUsedSet    = new Set();

      pages.forEach((page) => {
        (page.objectList || []).forEach((obj) => {
          if (!obj || obj.type !== 'text') {
            return;
          }
          const leftPanel = obj.valuesForLeftPanel || {};
          [obj.fontFamily, leftPanel.fontFamily].forEach((f) => {
            const normalized = this.normalizeFontName(f);
            if (normalized && normalized !== 'none') {
              fontsUsedSet.add(normalized);
            }
          });
        });
      });
      return Array.from(fontsUsedSet);
    },

    getOptions() {
      const allFonts = cloneDeep(this.$store.getters.getproductfontarray);

      const productFontsOrdered = this.fontsUsed.map((fontName) => {
        const matchingFont = allFonts.find(f => f.name === fontName) || {};
        return {
          name:        fontName,
          id:          matchingFont.id || '',
          isFavourite: matchingFont.isFavourite || false,
        };
      });

      const selectedFontName = this.getValue.name;
      if (selectedFontName) {
        const selectedIndex = productFontsOrdered.findIndex(f => f.name === selectedFontName);
        if (selectedIndex > 0) {
          const [selectedFont] = productFontsOrdered.splice(selectedIndex, 1);
          productFontsOrdered.unshift(selectedFont);
        }
      }

      const favoriteFonts = allFonts.filter(f => f.isFavourite && !this.fontsUsed.includes(f.name));
      const otherFonts    = allFonts.filter(
          f => !this.fontsUsed.includes(f.name) && !favoriteFonts.some(ff => ff.name === f.name),
      );

      const optionsWithSections = [];
      if (productFontsOrdered.length) {
        optionsWithSections.push({ isSection: true, label: 'Product Fonts' });
        optionsWithSections.push(...productFontsOrdered);
      }
      if (favoriteFonts.length) {
        optionsWithSections.push({ isSection: true, label: 'Favorite Fonts' });
        optionsWithSections.push(...favoriteFonts);
      }
      if (otherFonts.length) {
        optionsWithSections.push({ isSection: true, label: 'All Fonts' });
        optionsWithSections.push(...otherFonts);
      }

      return optionsWithSections;
    },

    // getOptions filtered by the search box; a section label is kept only when it still has fonts under it.
    filteredOptions() {
      const query = this.search.trim().toLowerCase();
      if (!query) {
        return this.getOptions;
      }
      const result = [];
      let section  = null;
      this.getOptions.forEach((option) => {
        if (option.isSection) {
          section = option;
          return;
        }
        if (option.name && option.name.toLowerCase().indexOf(query) !== -1) {
          if (section) {
            result.push(section);
            section = null;
          }
          result.push(option);
        }
      });
      return result;
    },
  },
  mounted() {
    this.options = this.$store.getters.getproductfontarray.map(element => ({
      name:        element.name,
      isFavourite: element.isFavourite,
      id:          element.id,
    }));
  },
  created() {
    this.options = this.getOptions;
  },
  beforeDestroy() {
    if (this.isPanelOpen) {
      this.closePanel();
    }
  },
  methods: {
    previewFontFamily(option) {
      if (!option || !option.name) {
        return;
      }
      EventBus.$emit(
          ApplicationEventTypes.LeftPanelEvents.PREVIEW_FONT_FAMILY,
          this.normalizeFontName(option.name),
      );
    },
    removePreviewFont() {
      EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CLEAR_FONT_FAMILY_PREVIEW);
    },
    normalizeFontName(fontFamilyValue) {
      if (typeof fontFamilyValue !== 'string') {
        return '';
      }
      return fontFamilyValue
          .replace(/"/g, '')
          .split(',')[0]
          .trim();
    },
    changeFavourite(fontId, isFavourite) {
      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);
      const userId             = newUrlSearchParams.has('userId') ? newUrlSearchParams.get('userId') : null;
      const domainUrl          = this.$store.getters.getBMCInstance === 'marketingcenter.source4' ? this.$store.getters.getBMCInstance : `www.${this.$store.getters.getBMCInstance}`;
      const url                = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=SetFontAsFavourite&bankId=${this.$store.getters.getBankId}&domainURL=https://${domainUrl}.com/&fontId=${fontId}&userId=${userId}&isFavourite=${isFavourite}`;
      axios.get(url)
          .then((response) => {
            console.log(response);
            const parser       = new DOMParser();
            const xmlParsed    = parser.parseFromString(response.data, 'text/xml');
            const result       = xmlParsed.getElementsByTagName('SetFontAsFavouriteResult');
            const parsedResult = JSON.parse(result[0].textContent);
            if (parsedResult) {
              this.$store.commit('changeFontFavouriteAttribute', { fontId, isFavourite });
            }
          })
          .catch((error) => {
            console.error(error);
          });
    },
    changeFontName(e) {
      if (!e.name) {
        return;
      }
      EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_FONT_FAMILY, e.name);
    },
    // ---- variant="panel" ----
    togglePanel() {
      if (this.isPanelOpen) {
        this.closePanel();
        return;
      }
      this.search      = '';
      this.isPanelOpen = true;
      document.addEventListener('mousedown', this.onDocumentMouseDown, true);
      this.$nextTick(() => {
        if (this.$refs.search) {
          this.$refs.search.focus();
        }
      });
    },
    closePanel() {
      this.isPanelOpen = false;
      document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
      if (this.previewName) {
        this.onPanelPreviewEnd();
      }
    },
    onDocumentMouseDown(e) {
      if (!this.$el.contains(e.target)) {
        this.closePanel();
      }
    },
    onPanelPreview(option) {
      this.previewName = option.name;
      this.previewFontFamily(option);
    },
    onPanelPreviewEnd() {
      this.previewName = '';
      this.removePreviewFont();
    },
    pickFont(option) {
      this.previewName = ''; // the canvas drops the preview itself when the font is applied
      this.changeFontName(option);
      this.closePanel();
    },
    panelSectionLabel(label) {
      return PANEL_SECTION_LABELS[label] || label;
    },
    reomveDuplicationFromArrayOfObject(objectArray) {
      return [...objectArray.reduce((map, obj) => map.set(obj.name, obj), new Map()).values()];
    },


  },
};
</script>
<style lang="scss">

.multiselect {
  &:hover {
    .multiselect__tags {
      border-color: $grey40;
    }
  }

  span {
    &.star {
      position: absolute;
      right: -25px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  min-height: 36px;

  .multiselect__tags {
    height: 36px;
    min-height: 0px;
    border: 1px solid $grey20;
    padding: 6px 16px;
  }

  .multiselect__content-wrapper {
    overflow-x: hidden;
  }

  .multiselect__option--highlight {
    background-color: $blue10;
    color: $blue50;
  }


  .multiselect__input {
    border: none;
    font-size: 14px;
    padding: 0px;
    margin: 0;
    line-height: normal;
    min-height: 0;

    &::placeholder {
      font-size: 14px;
      line-height: 24px;
      color: $grey40;
      cursor: default;
    }
  }

  .multiselect__select {
    &:before {
      top: auto;
      margin-top: 0px;
      border: none;
      background-image: url('../../assets/images/Vector.png');
      height: 16px;
      width: 16px;
      background-repeat: no-repeat;
      background-position: center center;
    }

    height: 16px;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    bottom: 0px;
    width: 16px;
    padding: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
  }

  .multiselect__placeholder {
    color: $grey40;
    padding: 0px;
    margin-bottom: 0px;
  }

  .multiselect__single {
    font-size: 14px;
    line-height: 24px;
    color: $grey80;
    margin: 0;
    padding: 0;
  }

  .multiselect__element {
    font-size: 14px;
    line-height: 24px;
    color: $grey80;
    width: 90%;
  }

  .multiselect__option {
    padding: 6px 16px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    white-space: normal;
  }

  .multiselect__content-wrapper {
    border-radius: 0px 0px 4px 4px;
  }

  .multiselect__option {
    font-size: 14px;
  }

  .multiselect__option--selected {
    color: $blue10;
    background-color: $blue50;
    font-weight: normal;

    &:after {
      content: '\2713';
      color: white;
    }

    &.multiselect__option--highlight {
      &::after {
        background: $blue50;
      }

    }
  }
}


.multiselect--active {
  &:hover {
    .multiselect__tags {
      border-color: $blue50;
    }
  }

  .multiselect__tags {
    border-color: $blue50;
    border-bottom: 0px;
  }

  .multiselect__content-wrapper {
    border: 1px solid $blue50;
  }

  .multiselect__select {
    &:before {
      transform: rotate(180deg);
    }
  }

  .multiselect__placeholder {
    margin-bottom: 0px;
    padding-bottom: 0px;
  }

}

.dropdown-selection-font {
  visibility: hidden !important;
}

.show-content {
  .dropdown-selection {
    visibility: visible !important;
  }

  .custom-icon {
    transform: rotate(-180deg) !important;
  }
}

.height {
  height: 180px;
  overflow-y: scroll;
}

.font-section-label {
  color: #344054;
  font-family: sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  border-bottom: 2px solid;
}

.full-option {
  width: 100%;
  height: 100%;
}

.option-content {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}
</style>
<style lang="scss" scoped>
@import '../LeftSection/styles/lp-tokens';

.lp-font {
  position: relative;
}

.lp-font__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: $lp-control-h;
  padding: 0 10px 0 12px;
  border: 1px solid transparent;
  border-radius: $lp-radius-control;
  background: $lp-fill;
  font-family: $lp-sans;
  font-size: 13.5px;
  color: $lp-ink;
  cursor: pointer;

  &:hover {
    border-color: $lp-line;
  }

  &.is-open {
    border-color: $lp-accent;

    .lp-font__chevron {
      transform: rotate(180deg);
    }
  }
}

.lp-font__current {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lp-font__chevron {
  min-width: 0;
  min-height: 0;
  transition: transform 0.15s;
}

.lp-font__pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  max-height: 320px;
  padding: 12px 8px 8px;
  border: 1px solid $lp-line;
  border-radius: $lp-radius-panel;
  background: $white;
  box-shadow: $lp-shadow-pop;
  font-family: $lp-sans;
}

.lp-font__search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  margin: 0 4px 8px;
  padding: 0 10px;
  border-radius: $lp-radius-control;
  background: $lp-fill;

  .icon {
    min-width: 0;
    min-height: 0;
    opacity: 0.6;
  }

  input {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: $lp-fs-control;
    color: $lp-ink;
  }
}

.lp-font__list {
  overflow-y: auto;
}

.lp-font__section {
  padding: 10px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: $lp-muted;
  text-transform: uppercase;
}

.lp-font__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: auto;
  min-height: 38px;
  padding: 4px 8px 4px 30px;
  border-radius: $lp-radius-control;
  cursor: pointer;

  &:hover,
  &.is-preview {
    background: $lp-fill;
  }
}

.lp-font__check {
  position: absolute;
  left: 9px;
  width: 15px;
  height: 15px;
  color: $lp-accent;
}

.lp-font__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: 16px;
  color: $lp-ink;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lp-font__badge {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 6px;
  background: $lp-accent-soft;
  font-size: 11px;
  font-weight: 600;
  color: $lp-accent-ink;
}

.lp-font__star {
  display: flex;
  flex: 0 0 auto;
  opacity: 0.55;

  &:hover,
  &.is-favourite {
    opacity: 1;
  }

  .icon {
    min-width: 0;
    min-height: 0;
  }
}

.lp-font__empty {
  padding: 12px;
  font-size: $lp-fs-control;
  color: $lp-muted;
}
</style>
